/** @jsxImportSource @emotion/react */
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import isEqual from 'lodash/isEqual';
import { Button } from '@mui/material';
import { IconButton } from 'components/buttons/IconSVG';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchCols,
  SearchRows,
} from 'components/layouts/SearchBox';
import CustomDialog from 'components/modals/common/CustomDialog';
import CustomGrid from 'components/grids/CustomGrid';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import {
  ControlBtnGroup,
  HalfContetntLayout,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import { ComboBox } from 'components/selects/ComboBox';
import { Code } from 'models/common/CommonCode';
import { EquipmentMasterCondition } from 'models/common/popup/EquipmentMaster';
import { CrudCode } from 'models/common/Edit';
import { getCommonCodeNamesCondition } from 'apis/admin/CommonCode';
import { EquipmentGroupPopUp } from 'pages/common/popup/EquipmentGroupPopUp';
import { EquipmentGroupCondition } from 'models/common/popup/EquipmentGroup';
import { useLoading } from 'components/process/Loading';
import { useCommonModal } from 'hooks/useCommonModal';
import { findSetupEquipment } from 'apis/setup/SetupEquipment';
import { SetupEquipment, SetupEquipmentCondition } from 'models/setup/SetupEquipment';
import { findExcpByElmPrjId } from 'apis/setup/SetupSchedule';
import { SetupSchedule } from 'models/setup/SetupSchedule';

/**
 * @description 셋업 설비추가 팝업 (설비셋업설비 - tb_eelmb_prj_tgt_eqp_m)
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 *  25.10.25    jshwang12      신규 생성
 */

interface Props {
  open: boolean;
  close: () => void;
  title: string;
  condition: {
    elmPrjId: string; // 프로젝트ID (필수)
    excpId?: string; // 협력사ID
    eqpId?: string; // 설비ID
    eqpNm?: string; // 설비명
    eqclId?: string; // 설비군
    prdnProcCd?: string; // 공정
  };
  defaultEquipment?: SetupEquipment | SetupEquipment[]; // 기존 선택된 설비정보 목록
  singleSelect?: boolean; // 단일선택 여부 (default: true)
  type?: 'SETUP'; // 설비팝업 유형
  onCallback: (result: SetupEquipment | SetupEquipment[]) => void;
}

const SetupEquipmentPopUp = ({
  open,
  close,
  title,
  singleSelect = true,
  onCallback,
  ...props
}: Props) => {
  const { t } = useTranslation();
  const searchRef = useRef<any>();
  const selectRef = useRef<any>();
  const [code, setCode] = useState<any>();
  const { openCommonModal } = useCommonModal();

  // 설비군팝업
  const [isOpenEquipmentGroupPopUp, setOpenEequipmentGroupPopUp] = useState<boolean>(false);

  const defaultCondition = Object.assign(
    {
      elmPrjId: '', // 프로젝트ID
      excpId: '', // 협력사ID
      eqpId: '', // 설비ID
      eqpNm: '', // 설비명
      eqclId: '', // 설비군
      prdnProcCd: '', // 공정
      useYn: 'Y',
    },
    {
      ...(props?.condition || {}),
    }
  );

  const [condition, setCondition] = useState<SetupEquipmentCondition>(defaultCondition);

  useEffect(() => {
    getCommonCodes();
  }, []);

  const getCommonCodes = async () => {
    const prdnProcCd = await getCommonCodeNamesCondition({
      // [25.10.25] 셋업관리인 경우 투자적용여부(옵션3) Y인 공정만 노출
      optValCtn3: 'Y',
      cmnGrCd: 'PRDN_PROC_CD',
    }); // 공정

    setCode({
      prdnProcCd: (prdnProcCd || [])
        // [25.10.25] 셋업관리인 경우 투자적용여부(옵션3) Y인 공정만 노출
        .filter((o) => (props.type === 'SETUP' ? o.optValCtn3 === 'Y' : true))
        .reduce(
          (acc, cur) => [
            ...acc,
            {
              ...cur,
              cmnCdNm: cur.cmnCdDesc,
            },
          ],
          [] as Code[]
        ),
    });
  };

  const handleConditionChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    setCondition({ ...condition, [e.target.name]: e.target.value });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      searchRef.current?.handleSearch(condition);
    }
  };

  const handleSearch = (condition: EquipmentMasterCondition) => {
    searchRef.current?.handleSearch(condition);
  };

  const handleConfirm = () => {
    if (singleSelect) {
      const items = searchRef.current?.getRowData();
      onCallback && onCallback((items || []).length ? items[0] : null);
    } else {
      if (!selectRef.current?.validate()) {
        return;
      }
      const items = selectRef.current?.getRowData();
      if (items.length === 0) {
        openCommonModal({
          modalType: 'confirm',
          content: String(
            t(
              'mp.msg.추가된 설비가 없습니다. 진행하시겠습니까?.',
              '추가된 설비가 없습니다. 진행하시겠습니까?'
            )
          ),
          yesCallback: () => {
            const updatedData = items.map((row) => ({
              ...row,
            }));

            onCallback && onCallback(updatedData);
            close();
          },
        });
        return false;
      }
      const updatedData = items.map((row) => ({
        ...row,
      }));

      onCallback && onCallback(updatedData);
    }
    close();
  };

  /**
   * Quick Select 이벤트 처리
   * @param item
   */
  const handleQuickSelect = (item: SetupEquipment) => {
    onCallback && onCallback(item);
    close();
  };

  const dialogButtons = [
    <Button
      key={'confirm'}
      css={IconButton.button}
      className="confirm"
      onClick={handleConfirm}
      disableRipple
    >
      {t('com.button.선택', '선택')}
    </Button>,
  ];
  return (
    <CustomDialog
      title={title}
      open={open}
      onClose={close}
      size="xl"
      onCancel={close}
      buttons={dialogButtons}
    >
      {/* 조회조건 시작 */}
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows className="FourCol">
              <SearchCols className="width33">
                <label>{t('asst.label.설비ID', '설비ID')}</label>
                <CustomInputWithSearch
                  name="eqpId"
                  placeholder={String(
                    t('setup.label.설비ID를 입력해 주세요.', '설비ID를 입력해 주세요.')
                  )}
                  value={condition?.eqpId}
                  onChange={handleConditionChange}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols className={'width25p'}>
                <label>{t('setup.label.설비명', '설비명')}</label>
                <CustomInputWithSearch
                  name="eqpNm"
                  placeholder={String(
                    t('setup.label.설비명을 입력해 주세요.', '설비명을 입력해 주세요.')
                  )}
                  value={condition?.eqpNm}
                  onChange={handleConditionChange}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols className={'width25p'}>
                <label>{String(t('com.label.설비군', '설비군'))}</label>
                <CustomInputWithSearch
                  name="eqclId"
                  className={'find'}
                  placeholder={String(
                    t('setup.label.설비군을 선택해 주세요.', '설비군을 선택해 주세요.')
                  )}
                  value={condition.eqclId}
                  onChange={handleConditionChange}
                  onSearchClick={() => setOpenEequipmentGroupPopUp(true)}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols className={'width25p'}>
                <label>{String(t('setup.label.공정', '공정'))}</label>
                <ComboBox
                  placeholder={String(
                    t('setup.label.공정을 선택해주세요.', '공정을 선택해주세요.')
                  )}
                  options={code?.prdnProcCd}
                  defaultValue={condition?.prdnProcCd}
                  onChange={(value) => setCondition({ ...condition, prdnProcCd: value as string })}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchBoxButtons
            setReset={() => setCondition(defaultCondition)}
            onSearch={() => handleSearch(condition)}
          />
        </SearchBoxRow>
      </SearchBox>
      {singleSelect ? (
        <SearchGrid
          ref={searchRef}
          singleSelect={singleSelect}
          defaultCondition={defaultCondition}
          onQuickSelect={handleQuickSelect}
        />
      ) : (
        <>
          <HalfContetntLayout style={{ marginTop: '10px' }}>
            <div>
              <SearchGrid
                ref={searchRef}
                singleSelect={singleSelect}
                defaultCondition={condition}
                addRow={(items) => selectRef.current?.addRowData(items)}
              />
            </div>

            <div>
              <SelectedGrid
                ref={selectRef}
                singleSelect={singleSelect}
                defaultCondition={condition}
                defaultEquipment={props?.defaultEquipment}
              />
            </div>
          </HalfContetntLayout>
        </>
      )}

      {isOpenEquipmentGroupPopUp && (
        <EquipmentGroupPopUp
          open={isOpenEquipmentGroupPopUp}
          close={() => setOpenEequipmentGroupPopUp(false)}
          condition={{
            equipmentGroup: condition.eqclId || '',
          }}
          onCallback={(value) =>
            setCondition({
              ...condition,
              eqclId: (value as EquipmentGroupCondition).equipmentGroup,
            })
          }
        />
      )}
    </CustomDialog>
  );
};

interface ContentProps {
  singleSelect: boolean;
  defaultCondition?: any;
  defaultEquipment?: SetupEquipment | SetupEquipment[];
  onQuickSelect?: (item: SetupEquipment) => void;
  addRow?: (items: SetupEquipment[]) => void;
}

const SearchGrid = forwardRef(
  ({ singleSelect, addRow, onQuickSelect, defaultCondition }: ContentProps, ref) => {
    const gridRef = useRef<any>();
    const prevCondition = useRef<any>();
    const { t } = useTranslation();
    const [prjExcpData, setPrjExcpData] = useState<SetupSchedule>();
    const [rowData, setRowData] = useState<SetupEquipment[]>([]);
    const [checkedItems, setCheckedItems] = useState<SetupEquipment[]>([]);
    const { openLoading } = useLoading();

    useImperativeHandle(ref, () => ({
      handleSearch: (condition) => {
        handleSearch(condition);
      },
      getRowData: () => {
        return checkedItems;
      },
    }));

    useEffect(() => {
      if (defaultCondition?.elmPrjId && defaultCondition?.excpId) {
        findExcpByElmPrjId(defaultCondition?.elmPrjId, defaultCondition?.excpId).then((result) => {
          if ((result || []).length > 0) {
            setPrjExcpData(result[0]);
          } else {
            setPrjExcpData({});
          }
        });
      }
    }, [defaultCondition?.elmPrjId, defaultCondition?.excpId]);

    useEffect(() => {
      if (!isEqual(prevCondition.current, defaultCondition)) {
        prevCondition.current = defaultCondition;
        handleSearch(defaultCondition);
      }
    }, []); //defaultCondition

    const handleSearch = (condition) => {
      openLoading(true);
      findSetupEquipment(condition)
        .then((result) => {
          setRowData(result || []);
        })
        .finally(() => openLoading(false));
    };

    const onInitialized = (grid) => {
      gridRef.current = grid;

      grid.hostElement.addEventListener('click', (e) => {
        if (grid.rows.length == 0) return;

        const ht = grid.hitTest(e);
        if (ht.row < 0 || ht.col < 0) return;

        const binding = grid.columns[ht.col].binding;
        const item = grid.rows[ht.row].dataItem;

        if (ht.panel === grid.cells) {
          // Quick Select
          if ('btnSelect' === binding) {
            onQuickSelect && onQuickSelect(item);
          }
        }
      });

      grid.hostElement.addEventListener('dblclick', (e) => {
        if (grid.rows.length == 0) return;

        const ht = grid.hitTest(e);
        if (ht.row < 0 || ht.col < 0) return;

        const item = grid.rows[ht.row].dataItem;

        if (item) {
          onQuickSelect && onQuickSelect(item);
        }
      });
    };

    const handleAddRow = () => {
      addRow && addRow(checkedItems);
    };

    const layoutDefinition = [
      {
        binding: 'btnSelect',
        header: ' ',
        cssClass: 'WijmoRelay',
        cellTemplate: '<Button />',
        width: 39,
        visible: singleSelect,
      },
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        width: 40,
        cellTemplate: (grid) => grid.row._idx + 1,
        visible: singleSelect,
      },
      {
        binding: 'eqpId',
        header: String(t('setup.label.M-CODE', 'M-CODE')),
        align: 'left',
        minWidth: 120,
        width: 150,
      },
      {
        binding: 'eqpNm',
        header: String(t('setup.label.설비명', '설비명')),
        align: 'left',
        minWidth: 120,
        width: '*',
      },
      {
        binding: 'eqclId',
        header: String(t('setup.label.설비군', '설비군')),
        align: 'center',
        minWidth: 100,
        width: 100,
      },
      {
        binding: 'prdnProcCdNm',
        header: String(t('setup.label.공정', '공정')),
        align: 'left',
        minWidth: 100,
        width: 150,
      },
      {
        binding: 'excpNm',
        header: String(t('setup.label.협력업체', '협력업체')),
        align: 'left',
        minWidth: 100,
        width: 150,
        visible: !defaultCondition?.excpId,
      },
      {
        binding: 'excpId',
        visible: false,
      },
    ];

    return (
      <>
        <SubTitleLayout>
          <SubTitleGroup>
            <h3>{t('asst.label.설비', '설비')}</h3>
            <span className="total">
              {t('com.label.총', '총')} <span>{(rowData || []).length.toLocaleString()}</span>
              {t('com.label.건', '건')}
            </span>
            {prjExcpData?.elmPrjNm && (
              <div className="info warning">
                {t('setup.label.프로젝트명', '프로젝트명')}: {prjExcpData?.elmPrjNm}
                {prjExcpData?.excpNm &&
                  `, ${t('setup.label.협력업체명', '협력업체명')}: ${prjExcpData?.excpNm}`}
              </div>
            )}
          </SubTitleGroup>
          {!singleSelect && (
            <ControlBtnGroup>
              <Button
                css={IconButton.button}
                className="addRow"
                onClick={handleAddRow}
                disableRipple
              >
                {t('com.label.추가', '추가')}
              </Button>
            </ControlBtnGroup>
          )}
        </SubTitleLayout>
        <CustomGrid
          layoutDefinition={layoutDefinition}
          rowData={rowData}
          height={450}
          isReadOnly={true}
          isFilter={false}
          autoCheck={true}
          rowSelection={singleSelect ? 'single' : 'multiple'}
          allowPinning={false}
          align="center"
          initialized={onInitialized}
          onChangeCheckedItem={(items) => setCheckedItems(items)}
        />
      </>
    );
  }
);

const SelectedGrid = forwardRef(
  ({ singleSelect, defaultCondition, defaultEquipment }: ContentProps, ref) => {
    const getDefaultEquipment = () => {
      if (singleSelect) {
        return defaultEquipment ? [defaultEquipment as SetupEquipment] : ([] as SetupEquipment[]);
      }
      return (defaultEquipment || []) as SetupEquipment[];
    };

    const gridRef = useRef<any>();
    const { t } = useTranslation();
    const [rowData, setRowData] = useState<SetupEquipment[]>(getDefaultEquipment());

    const [checkedItems, setCheckedItems] = useState<SetupEquipment[]>([]);

    useImperativeHandle(ref, () => ({
      addRowData: (items: SetupEquipment[]) => {
        addRowData(items);
      },
      getRowData: () => {
        return rowData;
      },
      validate: () => {
        return true;
      },
    }));

    const addRowData = (items: SetupEquipment[]) => {
      setRowData((prev) => {
        const newRows = (items || []).reduce((acc, cur) => {
          acc.push({
            ...cur,
            crudKey: CrudCode.CREATE,
          });
          return acc;
        }, [] as SetupEquipment[]);
        return [...prev, ...newRows].reduce((acc, cur) => {
          if (acc.findIndex(({ eqpId }) => eqpId === cur.eqpId) === -1) {
            acc.push(cur);
          }
          return acc;
        }, [] as SetupEquipment[]);
      });
    };

    const onInitialized = (grid) => {
      gridRef.current = grid;
    };

    const handleDelRow = () => {
      setRowData((prev) => (prev || []).filter((o) => !(checkedItems || []).includes(o)));
      setCheckedItems([]);
    };

    const layoutDefinition = [
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        minWidth: 40,
        width: 40,
        isReadOnly: true,
        cellTemplate: (grid) => grid.row._idx + 1,
      },
      {
        binding: 'eqpId',
        header: String(t('setup.label.M-CODE', 'M-CODE')),
        align: 'left',
        minWidth: 120,
        width: 150,
      },
      {
        binding: 'eqpNm',
        header: String(t('setup.label.설비명', '설비명')),
        align: 'left',
        minWidth: 120,
        width: '*',
      },
      {
        binding: 'eqclId',
        header: String(t('setup.label.설비군', '설비군')),
        align: 'center',
        minWidth: 100,
        width: 100,
      },
      {
        binding: 'prdnProcCdNm',
        header: String(t('setup.label.공정', '공정')),
        align: 'left',
        minWidth: 100,
        width: 150,
      },
      {
        binding: 'excpNm',
        header: String(t('setup.label.협력업체', '협력업체')),
        align: 'left',
        minWidth: 100,
        width: 150,
        visible: !defaultCondition?.excpId,
      },
      {
        binding: 'excpId',
        visible: false,
      },
    ];

    return (
      <>
        <SubTitleLayout>
          <SubTitleGroup>
            <h3></h3>
          </SubTitleGroup>
          <ControlBtnGroup>
            <Button
              css={IconButton.button}
              className="delRow"
              onClick={() => handleDelRow()}
              disableRipple
            >
              {t('com.button.삭제', '삭제')}
            </Button>
          </ControlBtnGroup>
        </SubTitleLayout>
        <CustomGrid
          layoutDefinition={layoutDefinition}
          rowData={rowData}
          height={450}
          isReadOnly={true}
          isFilter={false}
          autoCheck={true}
          rowSelection={'multiple'}
          allowPinning={false}
          allowSorting={false}
          align="center"
          initialized={onInitialized}
          onChangeCheckedItem={(items) => setCheckedItems(items)}
        />
      </>
    );
  }
);

SearchGrid.displayName = 'SearchGrid';
SelectedGrid.displayName = 'SelectedGrid';

export default SetupEquipmentPopUp;
